// /src/components/ProformaInvoice/ProformaInvoiceList.tsx
import React, { useState, useEffect } from 'react';
import {
  Search as SearchIcon,
  SlidersHorizontal as FilterIcon,
  Download as DownloadIcon,
  Mail as EmailIcon,
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  Eye as ViewIcon,
  FileText as PdfIcon,
  Plus as AddIcon,
  RefreshCw as RefreshIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ProformaInvoice, ProformaInvoiceStatus, ProformaInvoiceFilters, COMPANY_DATA } from '@/types/proformaInvoice';
import { useSupabaseProformaInvoices } from '@/hooks/useSupabaseProformaInvoices';
import { generateProformaInvoicePDF } from '@/utils/generateProformaInvoicePDF';
import { sendProformaEmail } from '@/utils/sendEmail';
import { ProformaInvoicePreview } from './ProformaInvoicePreview';
import { ProformaInvoiceForm } from './ProformaInvoiceForm';
import { Button } from '@/components/ui/button';

const statusConfig: Record<ProformaInvoiceStatus, { label: string; className: string }> = {
  draft: { label: 'Szkic', className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50' },
  sent: { label: 'Wysłana', className: 'bg-blue-500/20 text-blue-500 border-blue-500/50' },
  paid: { label: 'Zapłacona', className: 'bg-green-500/20 text-green-500 border-green-500/50' },
  cancelled: { label: 'Anulowana', className: 'bg-red-500/20 text-red-500 border-red-500/50' },
  overdue: { label: 'Po terminie', className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50' },
  pending: { label: 'Oczekująca', className: 'bg-amber-500/20 text-amber-500 border-amber-500/50' }
};

interface ProformaInvoiceListProps {
  onCreateNew?: () => void;
  qualityControlId?: string;
}

export const ProformaInvoiceList: React.FC<ProformaInvoiceListProps> = ({ 
  onCreateNew,
  qualityControlId 
}) => {
  const [invoices, setInvoices] = useState<ProformaInvoice[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<ProformaInvoiceFilters>({
    status: 'all',
    searchTerm: ''
  });
  const [selectedInvoice, setSelectedInvoice] = useState<ProformaInvoice | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient: '',
    subject: '',
    body: ''
  });

  const { 
    getInvoices, 
    deleteInvoice, 
    updateInvoiceStatus, 
    queueEmail, 
    exportToCSV,
    loading,
    error 
  } = useSupabaseProformaInvoices();

  useEffect(() => {
    loadInvoices();
  }, [filters, qualityControlId]);

  const loadInvoices = async () => {
    const { data } = await getInvoices({
      ...filters,
      ...(qualityControlId && { qualityControlId })
    });
    if (data) {
      setInvoices(data);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, searchTerm: event.target.value }));
    setPage(0);
  };

  const handleStatusFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters(prev => ({ ...prev, status: event.target.value as ProformaInvoiceStatus | 'all' }));
    setPage(0);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGeneratePDF = async (invoice: ProformaInvoice) => {
    try {
      await generateProformaInvoicePDF(invoice);
    } catch (error) {
      console.error('Błąd generowania PDF:', error);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedInvoice) return;

    try {
      // Generuj PDF
      const pdf = await generateProformaInvoicePDF(selectedInvoice);
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      
      // Wyślij email przez backend
      const success = await sendProformaEmail(
        emailData.recipient,
        selectedInvoice.invoiceNumber,
        emailData.subject,
        emailData.body,
        pdfBase64
      );

      if (success) {
        // Zapisz informację o wysłaniu w bazie
        const { error } = await queueEmail(
          selectedInvoice.id,
          emailData.recipient,
          emailData.subject,
          emailData.body
        );

        if (!error) {
          alert('Email został wysłany pomyślnie!');
          setEmailDialog(false);
          loadInvoices();
        } else {
          alert('Email został wysłany, ale nie udało się zapisać informacji w bazie');
        }
      } else {
        alert('Błąd podczas wysyłania emaila. Sprawdź konfigurację SMTP.');
      }
    } catch (error) {
      console.error('Błąd wysyłania emaila:', error);
      alert('Wystąpił błąd podczas wysyłania emaila');
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;

    const { error } = await deleteInvoice(selectedInvoice.id);
    if (!error) {
      setShowDeleteConfirm(false);
      setSelectedInvoice(null);
      loadInvoices();
    }
  };

  const handleStatusChange = async (invoice: ProformaInvoice, newStatus: ProformaInvoiceStatus) => {
    const { error } = await updateInvoiceStatus(invoice.id, newStatus);
    if (!error) {
      loadInvoices();
    }
  };

  const handleExportCSV = async () => {
    await exportToCSV(filters);
  };

  const openEmailDialog = (invoice: ProformaInvoice) => {
    setSelectedInvoice(invoice);
    setEmailData({
      recipient: invoice.clientEmail || '',
      subject: `Faktura proforma ${invoice.invoiceNumber}`,
      body: `Szanowni Państwo,\n\nW załączeniu przesyłamy fakturę proforma nr ${invoice.invoiceNumber}.\n\nPozdrawiamy,\n${COMPANY_DATA.name}`
    });
    setEmailDialog(true);
  };

  const totalPages = Math.ceil(invoices.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedInvoices = invoices.slice(startIndex, endIndex);

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500">
          {error}
        </div>
      )}

      <div className="bg-zinc-800 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[300px]">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="Szukaj po numerze, kliencie, NIP..."
              value={filters.searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-zinc-400 focus:outline-none focus:border-orange-500"
            />
          </div>

          <select
            value={filters.status}
            onChange={handleStatusFilter}
            className="px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
          >
            <option value="all">Wszystkie statusy</option>
            {Object.entries(statusConfig).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </select>

          <div className="flex-1" />

          <Button
            variant="secondary"
            onClick={loadInvoices}
            disabled={loading}
          >
            <RefreshIcon size={20} />
            Odśwież
          </Button>

          <Button
            variant="secondary"
            onClick={handleExportCSV}
          >
            <DownloadIcon size={20} />
            Eksportuj CSV
          </Button>

          {onCreateNew && (
            <Button
              variant="primary"
              onClick={onCreateNew}
            >
              <AddIcon size={20} />
              Nowa faktura
            </Button>
          )}
        </div>
      </div>

      <div className="bg-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left p-4 text-zinc-400 font-medium">Numer faktury</th>
              <th className="text-left p-4 text-zinc-400 font-medium">Data wystawienia</th>
              <th className="text-left p-4 text-zinc-400 font-medium">Klient</th>
              <th className="text-left p-4 text-zinc-400 font-medium">NIP</th>
              <th className="text-right p-4 text-zinc-400 font-medium">Kwota brutto</th>
              <th className="text-left p-4 text-zinc-400 font-medium">Status</th>
              <th className="text-left p-4 text-zinc-400 font-medium">Termin płatności</th>
              <th className="text-center p-4 text-zinc-400 font-medium">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {displayedInvoices.map((invoice) => (
              <tr key={invoice.id} className="border-b border-zinc-700 hover:bg-zinc-700/50">
                <td className="p-4">
                  <div className="font-medium text-white">
                    {invoice.invoiceNumber}
                  </div>
                </td>
                <td className="p-4 text-white">
                  {new Date(invoice.issueDate).toLocaleDateString('pl-PL')}
                </td>
                <td className="p-4">
                  <div className="text-white">{invoice.clientName}</div>
                  <div className="text-sm text-zinc-400">{invoice.clientCity}</div>
                </td>
                <td className="p-4 text-white">{invoice.clientNIP}</td>
                <td className="p-4 text-right">
                  <div className="font-medium text-white">
                    {invoice.totalGross.toFixed(2)} zł
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusConfig[invoice.status].className}`}>
                    {statusConfig[invoice.status].label}
                  </span>
                </td>
                <td className="p-4">
                  <div className="text-white">
                    {new Date(invoice.paymentDeadline).toLocaleDateString('pl-PL')}
                  </div>
                  {invoice.status === 'sent' && 
                   new Date(invoice.paymentDeadline) < new Date() && (
                    <div className="text-xs text-red-500">
                      Po terminie
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex justify-center gap-1">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowPreview(true);
                      }}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-600 rounded transition-colors"
                      title="Podgląd"
                    >
                      <ViewIcon size={16} />
                    </button>

                    <button
                      onClick={() => handleGeneratePDF(invoice)}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-600 rounded transition-colors"
                      title="Generuj PDF"
                    >
                      <PdfIcon size={16} />
                    </button>

                    {invoice.clientEmail && (
                      <button
                        onClick={() => openEmailDialog(invoice)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-600 rounded transition-colors"
                        title="Wyślij email"
                      >
                        <EmailIcon size={16} />
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowEdit(true);
                      }}
                      disabled={invoice.status !== 'draft'}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Edytuj"
                    >
                      <EditIcon size={16} />
                    </button>

                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowDeleteConfirm(true);
                      }}
                      disabled={invoice.status !== 'draft'}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Usuń"
                    >
                      <DeleteIcon size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span className="text-zinc-400">Wierszy na stronę:</span>
          <select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            className="bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white focus:outline-none focus:border-orange-500"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-zinc-400">
            {startIndex + 1}-{Math.min(endIndex, invoices.length)} z {invoices.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Dialog podglądu */}
      {showPreview && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPreview(false)}
          />
          <div className="relative bg-zinc-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <h2 className="text-xl font-semibold text-white">
                Podgląd faktury {selectedInvoice.invoiceNumber}
              </h2>
            </div>
            <div className="p-6">
              <ProformaInvoicePreview 
                invoice={selectedInvoice} 
                onClose={() => setShowPreview(false)}
              />
            </div>
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowPreview(false)}>
                Zamknij
              </Button>
              <Button variant="primary" onClick={() => handleGeneratePDF(selectedInvoice)}>
                <PdfIcon size={20} />
                Generuj PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog edycji */}
      {showEdit && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowEdit(false)}
          />
          <div className="relative bg-zinc-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <h2 className="text-xl font-semibold text-white">
                Edytuj fakturę {selectedInvoice.invoiceNumber}
              </h2>
            </div>
            <div className="p-6">
              <ProformaInvoiceForm
                onSubmit={async (data) => {
                  // TODO: Implementacja aktualizacji
                  setShowEdit(false);
                  loadInvoices();
                }}
                onCancel={() => setShowEdit(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Dialog potwierdzenia usunięcia */}
      {showDeleteConfirm && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-zinc-900 rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              Potwierdź usunięcie
            </h3>
            <p className="text-zinc-300 mb-6">
              Czy na pewno chcesz usunąć fakturę {selectedInvoice.invoiceNumber}?
              Tej operacji nie można cofnąć.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Anuluj
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Usuń
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog wysyłki email */}
      {emailDialog && selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setEmailDialog(false)}
          />
          <div className="relative bg-zinc-900 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              Wyślij fakturę email
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Odbiorca
                </label>
                <input
                  type="email"
                  value={emailData.recipient}
                  onChange={(e) => setEmailData(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Temat
                </label>
                <input
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Treść
                </label>
                <textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={() => setEmailDialog(false)}>
                Anuluj
              </Button>
              <Button variant="primary" onClick={handleSendEmail}>
                Wyślij
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
